<template>
  <!-- Desktop: teleport into navbar so panel slides down from search bar (no overlay). Mobile: full overlay. -->
  <Teleport :to="teleportTarget">
    <!-- Mobile: overlay + box so user can type in modal -->
    <Transition v-if="isMobile" name="search-modal">
      <div v-if="modelValue" class="search-modal-overlay" role="dialog" aria-modal="true"
        aria-label="Search bonds and fixed deposits" @click.self="close">
        <div class="search-modal-box" :style="modalBoxStyle">
          <div class="search-modal-header" :style="{ borderBottomColor: colors.borderColor }">
            <div class="search-input-wrap" :style="{ backgroundColor: colors.inputBg }">
              <ClientOnly>
                <v-icon aria-hidden="true" class="search-icon" size="22">mdi-magnify</v-icon>
              </ClientOnly>
              <input ref="inputRef" v-model="query" type="search" class="search-input" :placeholder="placeholder"
                aria-label="Search bonds or fixed deposits" autocomplete="off" @keydown.esc="close" />
              <button type="button" class="search-close" aria-label="Close" @click="close">×</button>
            </div>
          </div>
          <div class="search-modal-body">
            <template v-if="loading && query.trim().length >= 2">
              <div class="search-results-wrap search-results-wrap--mobile search-skeleton" aria-hidden="true">
                <nav class="search-sidebar">
                  <div class="search-skeleton-cat" />
                  <div class="search-skeleton-cat search-skeleton-cat--short" />
                </nav>
                <div class="search-results-panel">
                  <div v-for="i in 5" :key="i" class="search-skeleton-row">
                    <div class="search-skeleton-main">
                      <div class="search-skeleton-line search-skeleton-line--title" />
                      <div class="search-skeleton-line search-skeleton-line--meta" />
                    </div>
                    <div class="search-skeleton-yield" />
                  </div>
                </div>
              </div>
            </template>
            <template
              v-else-if="!loading && query.trim().length >= 2 && (bondResults.length > 0 || fdResults.length > 0)">
              <div class="search-results-wrap" :class="{ 'search-results-wrap--mobile': isMobile }">
                <nav class="search-sidebar" aria-label="Result categories">
                  <button v-for="cat in categories" :key="cat.id" type="button" class="search-cat-btn"
                    :class="{ 'search-cat-btn--active': selectedCategory === cat.id }"
                    :style="selectedCategory === cat.id ? { backgroundColor: '#EAF7FF' } : {}"
                    @click="selectedCategory = cat.id">
                    <span class="search-cat-label">{{ cat.label }}</span>
                    <span class="search-cat-count">({{ cat.count }})</span>
                  </button>
                </nav>
                <div class="search-results-panel">
                  <template v-if="selectedCategory === 'bonds'">
                    <ul class="search-list">
                      <li v-for="item in bondResults" :key="item.slug" class="search-item search-item-bond"
                        @click="onBondClick(item)">
                        <div class="search-item-main">
                          <span class="search-item-title">{{ item.issuerName }}</span>
                          <span class="search-item-meta">{{ item.isin }}</span>
                        </div>
                        <div class="search-item-right">
                          <span class="search-item-yield">{{ yieldLabel(item) }}</span>
                        </div>
                      </li>
                    </ul>
                  </template>
                  <template v-else-if="selectedCategory === 'fds'">
                    <ul class="search-list">
                      <li v-for="fd in fdResults" :key="fd.name" class="search-item search-item-fd" @click="onFdClick(fd)">
                        <div class="search-item-main">
                          <span class="search-item-title">{{ fd.name }}</span>
                          <span class="search-item-meta">FD</span>
                        </div>
                        <div class="search-item-right">
                          <span class="search-item-yield">{{ fd.maxInterest }}% p.a.</span>
                        </div>
                      </li>
                    </ul>
                  </template>
                </div>
              </div>
            </template>
            <div v-else-if="query.trim().length >= 2 && bondResults.length === 0 && fdResults.length === 0"
              class="search-empty">
              No results for "{{ query.trim() }}"
            </div>
            <div v-else-if="query.trim().length === 1" class="search-hint">Type at least 2 characters to search</div>
            <div v-else class="search-hint">Search by issuer name or ISIN (bonds), or issuer name (FDs)</div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Desktop: dropdown only, no overlay — slides down from search bar -->
    <Transition v-else name="search-dropdown">
      <div v-if="modelValue" ref="dropdownRef" class="search-dropdown-panel" role="listbox" aria-label="Search results"
        :style="modalBoxStyle">
        <div class="search-modal-body">
          <template v-if="loading && query.trim().length >= 2">
            <div class="search-two-col search-skeleton" aria-hidden="true">
              <nav class="search-sidebar">
                <div class="search-skeleton-cat" />
                <div class="search-skeleton-cat search-skeleton-cat--short" />
              </nav>
              <div class="search-results-panel">
                <div v-for="i in 5" :key="i" class="search-skeleton-row">
                  <div class="search-skeleton-line search-skeleton-line--title" />
                  <div class="search-skeleton-line search-skeleton-line--meta" />
                  <div class="search-skeleton-yield" />
                </div>
              </div>
            </div>
          </template>
          <template
            v-else-if="!loading && query.trim().length >= 2 && (bondResults.length > 0 || fdResults.length > 0)">
            <div class="search-two-col">
              <nav class="search-sidebar" aria-label="Result categories">
                <button v-for="cat in categories" :key="cat.id" type="button" class="search-cat-btn"
                  :class="{ 'search-cat-btn--active': selectedCategory === cat.id }"
                  :style="selectedCategory === cat.id ? { backgroundColor: '#EAF7FF' } : {}"
                  @click="selectedCategory = cat.id">
                  <span class="search-cat-label">{{ cat.label }}</span>
                  <span class="search-cat-count">({{ cat.count }})</span>
                </button>
              </nav>
              <div class="search-results-panel">
                <template v-if="selectedCategory === 'bonds'">
                  <ul class="search-list">
                    <li v-for="item in bondResults" :key="item.slug" class="search-item search-item-bond"
                      @click="onBondClick(item)" tabindex="0" role="option" :aria-selected="false">
                      <div class="search-item-main">
                        <span class="search-item-title">{{ item.issuerName }}</span>
                        <span class="search-item-meta">{{ item.isin }}</span>
                      </div>
                      <div class="search-item-right">
                        <span class="search-item-yield">{{ yieldLabel(item) }}</span>
                      </div>
                    </li>
                  </ul>
                </template>
                <template v-else-if="selectedCategory === 'fds'">
                  <ul class="search-list">
                    <li v-for="fd in fdResults" :key="fd.name" class="search-item search-item-fd" @click="onFdClick(fd)">
                      <div class="search-item-main">
                        <span class="search-item-title">{{ fd.name }}</span>
                        <span class="search-item-meta">FD</span>
                      </div>
                      <div class="search-item-right">
                        <span class="search-item-yield">{{ fd.maxInterest }}% p.a.</span>
                      </div>
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </template>
          <div v-else-if="query.trim().length >= 2 && bondResults.length === 0 && fdResults.length === 0"
            class="search-empty">
            No results for "{{ query.trim() }}"
          </div>
          <div v-else-if="query.trim().length === 1" class="search-hint">Type at least 2 characters to search</div>
          <div v-else class="search-hint">Search by issuer name or ISIN (bonds), or issuer name (FDs)</div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useTheme } from "~/composables/useTheme";
import { useUtmTracking } from "~/composables/useUtmTracking";
import bondsService from "~/service/bondsService";
import analytics from "~/services/analytics";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const router = useRouter();
const route = useRoute();
const { colors } = useTheme();
const { buildRedirectUrl } = useUtmTracking();
const { investorUrl } = useInvestorPlatform();

const placeholder = "Search bonds or fixed deposits...";
const query = useState("global-search-query", () => "");
const inputRef = ref<HTMLInputElement | null>(null);
const isMobile = ref(false);

const modalBoxStyle = computed(() => ({
  backgroundColor: colors.primaryBg,
  color: colors.text,
  border: `1px solid ${colors.borderColor}`,
}));

const teleportTarget = computed(() => (isMobile.value ? "body" : "#search-dropdown-anchor"));

const dropdownRef = ref<HTMLElement | null>(null);

onMounted(() => {
  const mq = window.matchMedia("(max-width: 1024px)");
  isMobile.value = mq.matches;
  mq.addEventListener("change", (e) => { isMobile.value = e.matches; });
});
const loading = ref(false);
const bondResults = ref<Array<{
  type: string;
  isin: string;
  issuerName: string;
  slug: string;
  listedYield?: number;
  couponRate?: number;
}>>([]);
const fdSources = ref<any[]>([]);
const fdResults = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (q.length < 2 || !fdSources.value.length) return [];
  return fdSources.value
    .filter((s) => (s.name || "").toLowerCase().includes(q))
    .slice(0, 8)
    .map((s) => ({ name: s.name, maxInterest: s.maxInterest, fIssuerId: s.fIssuerId }));
});

type CategoryId = "bonds" | "fds";
const selectedCategory = ref<CategoryId>("bonds");

const categories = computed(() => {
  const list: { id: CategoryId; label: string; count: number }[] = [];
  if (bondResults.value.length > 0) {
    list.push({ id: "bonds", label: "Bonds", count: bondResults.value.length });
  }
  if (fdResults.value.length > 0) {
    list.push({ id: "fds", label: "FD", count: fdResults.value.length });
  }
  return list;
});

watch(
  [bondResults, fdResults],
  () => {
    if (categories.value.length === 0) return;
    const ids = categories.value.map((c) => c.id);
    if (!ids.includes(selectedCategory.value)) {
      selectedCategory.value = ids[0];
    } else if (ids[0] === "bonds") {
      selectedCategory.value = "bonds";
    }
  },
  { immediate: true }
);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 300;

function close() {
  emit("update:modelValue", false);
}

if (typeof window !== "undefined") {
  window.addEventListener('close-all-overlays', () => {
    close();
  });
}

function yieldLabel(item: { listedYield?: number; couponRate?: number }) {
  const y = item.listedYield ?? item.couponRate;
  if (y != null && !Number.isNaN(y)) return `${Number(y).toFixed(2)}%`;
  return "—";
}

async function runSearch() {
  const q = query.value.trim();
  if (q.length < 2) {
    bondResults.value = [];
    return;
  }
  loading.value = true;
  try {
    const res = await bondsService.searchBonds(q, 15);
    bondResults.value = (res as any)?.bonds ?? [];
    
    // Track search event in GTM
    analytics.track('search', {
      search_term: q,
      results_count: bondResults.value.length + fdResults.value.length
    })
  } catch (e) {
    console.error("Search failed:", e);
    bondResults.value = [];
  } finally {
    loading.value = false;
  }
}

function onBondClick(item: { slug: string; issuerName?: string; isin?: string }) {
  analytics.track('search_result_clicked', {
    search_term: query.value.trim(),
    result_type: 'bond',
    result_name: item.issuerName || item.slug,
    result_id: item.slug,
    isin: item.isin
  })
  query.value = "";
  router.push(`/products/bonds/${item.slug}`);
  close();
}

function onFdClick(fd?: { fIssuerId?: string; name?: string }) {
  analytics.track('search_result_clicked', {
    search_term: query.value.trim(),
    result_type: 'fd',
    result_name: fd?.name || fd?.fIssuerId,
    result_id: fd?.fIssuerId
  })
  query.value = "";
  const targetUrl = fd?.fIssuerId
    ? investorUrl(`/explore-products?tab=fds&fIssuerId=${encodeURIComponent(fd.fIssuerId)}`)
    : investorUrl("/explore-products?tab=fds");
  const url = buildRedirectUrl(targetUrl, route.path, "search-fd-click");
  window.open(url, "_blank");
  close();
}

function handleClickOutside(e: MouseEvent) {
  if (isMobile.value) return;
  const target = e.target as Node;
  const anchor = document.getElementById("search-dropdown-anchor");
  const wrap = document.querySelector(".navbar-search-wrap");
  if (anchor?.contains(target) || wrap?.contains(target)) return;
  close();
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      if (categories.value.length > 0) selectedCategory.value = categories.value[0].id;
      nextTick(() => {
        if (isMobile.value) inputRef.value?.focus();
      });
      if (!isMobile.value) {
        nextTick(() => {
          setTimeout(() => document.addEventListener("click", handleClickOutside), 0);
        });
      }
      if (fdSources.value.length === 0) {
        $fetch("https://prod-api.equirize.com/fd-cb/fd/sources")
          .then((r: any) => {
            fdSources.value = r?.sources ?? [];
          })
          .catch(() => {
            fdSources.value = [];
          });
      }
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }
);

watch(query, () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSearch, DEBOUNCE_MS);
  return () => {
    if (debounceTimer) clearTimeout(debounceTimer);
  };
});
</script>

<style scoped>
.search-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 72px 16px 24px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.search-modal-box {
  width: 100%;
  max-width: 640px;
  border-radius: 12px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
}

.search-two-col,
.search-results-wrap {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Mobile: single column — category pills on top, full-width results below */
.search-results-wrap--mobile {
  flex-direction: column;
}

.search-results-wrap--mobile .search-sidebar {
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
  border-right: none;
  border-bottom: 1px solid rgba(128, 128, 128, 0.2);
  padding: 10px 12px;
  gap: 8px;
}

.search-results-wrap--mobile .search-sidebar .search-cat-btn {
  width: auto;
  flex: 0 0 auto;
}

.search-sidebar {
  flex-shrink: 0;
  width: 112px;
  min-width: 112px;
  padding: 12px 10px 12px 12px;
  border-right: 1px solid rgba(128, 128, 128, 0.2);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Desktop: panel that slides down from navbar search bar (no overlay) */
.search-dropdown-panel {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: min(420px, 70vh);
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.search-dropdown-enter-active,
.search-dropdown-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.search-dropdown-enter-from,
.search-dropdown-leave-to {
  transform: translateY(-12px);
  opacity: 0;
}

.search-cat-btn {
  display: flex;
  align-items: baseline;
  gap: 6px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.search-cat-btn:hover {
  background: rgba(128, 128, 128, 0.12);
}

.search-cat-btn--active {
  background: #EAF7FF;
  font-weight: 600;
}

.search-cat-label {
  white-space: nowrap;
  min-width: 0;
  overflow: visible;
}

.search-cat-count {
  flex-shrink: 0;
  font-size: 12px;
  opacity: 0.85;
}

.search-results-panel {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 12px 16px;
}

.search-modal-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.2);
}

.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(128, 128, 128, 0.1);
}

.search-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 16px;
  color: inherit;
  outline: none;
}

.search-input::placeholder {
  color: rgba(128, 128, 128, 0.8);
}

.search-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.8;
  border-radius: 4px;
}

.search-close:hover {
  opacity: 1;
  background: rgba(128, 128, 128, 0.2);
}

.search-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
}

/* Skeleton loader: two-column layout with shimmer */
.search-skeleton .search-sidebar {
  gap: 8px;
}

.search-skeleton-cat {
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(90deg,
      rgba(128, 128, 128, 0.12) 25%,
      rgba(128, 128, 128, 0.2) 50%,
      rgba(128, 128, 128, 0.12) 75%);
  background-size: 200% 100%;
  animation: search-skeleton-shimmer 1.2s ease-in-out infinite;
}

.search-skeleton-cat--short {
  width: 60%;
}

.search-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 8px;
}

.search-skeleton-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.search-skeleton-row .search-skeleton-line,
.search-skeleton-row .search-skeleton-yield {
  border-radius: 4px;
  background: linear-gradient(90deg,
      rgba(128, 128, 128, 0.12) 25%,
      rgba(128, 128, 128, 0.2) 50%,
      rgba(128, 128, 128, 0.12) 75%);
  background-size: 200% 100%;
  animation: search-skeleton-shimmer 1.2s ease-in-out infinite;
}

.search-skeleton-line {
  height: 14px;
}

.search-skeleton-line--title {
  width: 70%;
  max-width: 200px;
}

.search-skeleton-line--meta {
  width: 40%;
  max-width: 100px;
  height: 10px;
}

.search-skeleton-yield {
  flex-shrink: 0;
  width: 48px;
  height: 16px;
}

@keyframes search-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.search-loading,
.search-hint,
.search-empty {
  padding: 24px 16px;
  text-align: center;
  color: rgba(128, 128, 128, 0.9);
  font-size: 14px;
}

.search-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.search-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.search-item:hover {
  background: rgba(128, 128, 128, 0.12);
}

.search-item-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-item-title {
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-item-meta {
  font-size: 12px;
  opacity: 0.7;
}

.search-item-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-item-yield {
  font-size: 13px;
  font-weight: 600;
}

.search-modal-enter-active,
.search-modal-leave-active {
  transition: opacity 0.2s ease;
}

.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
}

.search-modal-enter-active .search-modal-box,
.search-modal-leave-active .search-modal-box {
  transition: transform 0.2s ease;
}

.search-modal-enter-from .search-modal-box,
.search-modal-leave-to .search-modal-box {
  transform: translateY(-100%);
  opacity: 0.9;
}
</style>
